import React, { useState, useEffect } from 'react'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/solid'
import { folderApi } from '../../../../shared/services/folderApi'

interface Folder {
  _id: string
  name: string
  color: string
  description?: string
  parentFolder?: string
  children?: Folder[]
}

interface FolderSelectProps {
  value: string | null
  onChange: (folderId: string | null) => void
  disabled?: boolean
  placeholder?: string
}

// 트리 구조로 폴더를 렌더링하는 컴포넌트
const FolderTreeItem: React.FC<{
  folder: Folder
  depth: number
  selectedValue: string | null
  expandedFolders: Set<string>
  onSelect: (folderId: string) => void
  onToggleExpand: (folderId: string) => void
}> = ({
  folder,
  depth,
  selectedValue,
  expandedFolders,
  onSelect,
  onToggleExpand,
}) => {
  const hasChildren = folder.children && folder.children.length > 0
  const isExpanded = expandedFolders.has(folder._id)
  const isSelected = selectedValue === folder._id

  return (
    <div>
      <button
        type="button"
        onClick={() => onSelect(folder._id)}
        className={`flex w-full items-center px-3 py-2 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-600 ${
          isSelected
            ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
            : ''
        }`}
      >
        {/* 들여쓰기 */}
        <div
          style={{ marginLeft: `${depth * 16}px` }}
          className="flex min-w-0 flex-1 items-center"
        >
          {/* 확장/축소 버튼 */}
          {hasChildren ? (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                onToggleExpand(folder._id)
              }}
              className="mr-1 flex-shrink-0 rounded p-0.5 transition-colors hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              {isExpanded ? (
                <ChevronDownIcon className="h-3 w-3 text-gray-400" />
              ) : (
                <ChevronRightIcon className="h-3 w-3 text-gray-400" />
              )}
            </button>
          ) : (
            <div className="mr-1 w-4 flex-shrink-0" />
          )}

          {/* 폴더 색상 및 이름 */}
          <div
            className="mr-2 h-3 w-3 flex-shrink-0 rounded"
            style={{ backgroundColor: folder.color }}
          />
          <div className="min-w-0 flex-1">
            <span className="block truncate text-sm text-gray-900 dark:text-white">
              {folder.name}
            </span>
            {folder.description && (
              <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                {folder.description}
              </span>
            )}
          </div>
        </div>
      </button>

      {/* 서브 폴더들 */}
      {hasChildren && isExpanded && (
        <div>
          {folder.children!.map(child => (
            <FolderTreeItem
              key={child._id}
              folder={child}
              depth={depth + 1}
              selectedValue={selectedValue}
              expandedFolders={expandedFolders}
              onSelect={onSelect}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const FolderSelect: React.FC<FolderSelectProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = '폴더를 선택하세요',
}) => {
  const [folders, setFolders] = useState<Folder[]>([])
  const [folderTree, setFolderTree] = useState<Folder[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())

  useEffect(() => {
    fetchFolders()
  }, [])

  // 폴더들을 트리 구조로 변환하는 함수
  const buildFolderTree = (folders: Folder[]): Folder[] => {
    const folderMap = new Map<string, Folder>()
    const rootFolders: Folder[] = []

    // 먼저 모든 폴더를 맵에 저장하고 children 배열 초기화
    folders.forEach(folder => {
      folderMap.set(folder._id, { ...folder, children: [] })
    })

    // 트리 구조 구성
    folders.forEach(folder => {
      const folderWithChildren = folderMap.get(folder._id)!

      if (folder.parentFolder) {
        // 부모 폴더가 있는 경우 부모의 children에 추가
        const parent = folderMap.get(folder.parentFolder)
        if (parent && parent.children) {
          parent.children.push(folderWithChildren)
        } else {
          // 부모를 찾을 수 없으면 루트에 추가
          rootFolders.push(folderWithChildren)
        }
      } else {
        // 부모가 없으면 루트 폴더
        rootFolders.push(folderWithChildren)
      }
    })

    // 각 레벨에서 폴더를 이름순으로 정렬
    const sortFolders = (folders: Folder[]): void => {
      folders.sort((a, b) => a.name.localeCompare(b.name))
      folders.forEach(folder => {
        if (folder.children && folder.children.length > 0) {
          sortFolders(folder.children)
        }
      })
    }

    sortFolders(rootFolders)
    return rootFolders
  }

  const fetchFolders = async () => {
    setLoading(true)
    try {
      const fetchedFolders = (await folderApi.list()) as unknown as Folder[]
      setFolders(fetchedFolders)
      setFolderTree(buildFolderTree(fetchedFolders))
    } catch (error) {
      console.error('폴더 조회 실패:', error)
    } finally {
      setLoading(false)
    }
  }

  // 선택된 폴더 찾기 (트리 구조에서)
  const findSelectedFolder = (
    folders: Folder[],
    targetId: string | null
  ): Folder | null => {
    if (!targetId) {
      return null
    }

    for (const folder of folders) {
      if (folder._id === targetId) {
        return folder
      }
      if (folder.children) {
        const found = findSelectedFolder(folder.children, targetId)
        if (found) {
          return found
        }
      }
    }
    return null
  }

  const selectedFolder = findSelectedFolder(folders, value)

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  const handleSelect = (folderId: string | null) => {
    onChange(folderId)
    setIsOpen(false)
  }

  const handleToggleExpand = (folderId: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev)
      if (newSet.has(folderId)) {
        newSet.delete(folderId)
      } else {
        newSet.add(folderId)
      }
      return newSet
    })
  }

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      if (!target.closest('.folder-select-container')) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen])

  return (
    <div className="folder-select-container relative">
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`flex w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-left focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 ${
          disabled
            ? 'cursor-not-allowed bg-gray-50 opacity-50 dark:bg-gray-800'
            : 'hover:bg-gray-50 dark:hover:bg-gray-600'
        } ${
          isOpen
            ? 'border-blue-500 ring-2 ring-blue-500'
            : 'border-gray-300 dark:border-gray-600'
        }`}
      >
        <span className="flex items-center">
          {selectedFolder ? (
            <>
              <div
                className="mr-2 h-3 w-3 flex-shrink-0 rounded"
                style={{ backgroundColor: selectedFolder.color }}
              />
              <span className="truncate text-gray-900 dark:text-white">
                {selectedFolder.name}
              </span>
            </>
          ) : (
            <span className="text-gray-500 dark:text-gray-400">
              {placeholder}
            </span>
          )}
        </span>
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-400 transition-transform ${
            isOpen ? 'rotate-180 transform' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-700">
          {loading ? (
            <div className="px-3 py-2 text-center text-gray-500 dark:text-gray-400">
              로딩 중...
            </div>
          ) : (
            <>
              {/* 폴더 없음 옵션 */}
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className={`flex w-full items-center px-3 py-2 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-600 ${
                  value === null
                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                    : ''
                }`}
              >
                <div className="mr-2 h-3 w-3 rounded bg-gray-300 dark:bg-gray-500" />
                <span className="text-gray-700 dark:text-gray-300">
                  폴더 없음
                </span>
              </button>

              {/* 폴더 트리 */}
              {folderTree.map(folder => (
                <FolderTreeItem
                  key={folder._id}
                  folder={folder}
                  depth={0}
                  selectedValue={value}
                  expandedFolders={expandedFolders}
                  onSelect={handleSelect}
                  onToggleExpand={handleToggleExpand}
                />
              ))}

              {folderTree.length === 0 && (
                <div className="px-3 py-2 text-center text-gray-500 dark:text-gray-400">
                  폴더가 없습니다
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default FolderSelect
